import React, { useEffect, useState } from 'react';

import { SlideoutPanelComponent as Slideout } from 'edenred-ui';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import { goToPage } from '@epi-actions/navigation';
import { useGetDetailsQuery } from '@epi-api/user';
import {
  BenefitType,
  FundsLoadingTypes,
  LimitType
} from '@epi-constants/benefitTypes';
import { routerPaths } from '@epi-constants/routerPaths';
import { slideoutMode } from '@epi-constants/slideoutMode';
import { IBenefitGroupDetail } from '@epi-models/containers/ManageBenefitGroups';

import {
  ManageBenefitLunchFormFields,
  mapFormToBenefitGroup
} from '../../helpers';
import { BenefitGroupDetailActions } from '../BenefitGroupDetailActions';
import { BenefitGroupDetailContent } from '../BenefitGroupDetailContent';
import { CloseBenefitDetailModal } from '../CloseBenefitDetailModal';
import { ConfirmationModal } from '../ConfirmationModal';

export function BenefitGroupDetail({
  detail,
  benefitGroup,
  benefitType,
  dailyLimitOptions,
  onClose,
  onSave,
  onCreate,
  onCancel,
  onEdit
}: IBenefitGroupDetail) {
  const [showCloseConfirmation, setShowCloseConfirmation] = useState(false);
  const [showRedirectConfirmation, setShowRedirectConfirmation] =
    useState(false);
  const [isSubmitOnRedirectConfirmation, setIsSubmitOnRedirectConfirmation] =
    useState(false);
  const [isExistingClients, setIsExistingClients] = useState(false);
  const [
    showConfirmationModalForExistingClients,
    setShowConfirmationModalForExistingClients
  ] = useState(false);
  const { isLoading, data: customerData } = useGetDetailsQuery();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const form = useForm({
    mode: 'onChange',
    defaultValues: {
      [ManageBenefitLunchFormFields.BenefitGroupName]: benefitGroup
        ? benefitGroup.groupName
        : '',
      [ManageBenefitLunchFormFields.DailyLimit]: benefitGroup
        ? benefitGroup.maxAmountPerPeriod
        : dailyLimitOptions[0]?.id,
      [ManageBenefitLunchFormFields.FundsLoadingType]: benefitGroup
        ? benefitGroup.fundsLoadingType
        : '',
      [ManageBenefitLunchFormFields.AutomaticRepeat]: benefitGroup
        ? benefitGroup.recurringTopupRequestEnabled
        : false,
      [ManageBenefitLunchFormFields.IsMaximumSpendingLimit]:
        benefitGroup?.isMaximumSpendingLimit === undefined
          ? LimitType.maximum
          : benefitGroup?.isMaximumSpendingLimit
          ? LimitType.maximum
          : LimitType.custom
    }
  });

  const {
    formState: { isValid, isDirty, dirtyFields },
    watch,
    getValues,
    reset
  } = form;

  const fundsLoadingType = watch(ManageBenefitLunchFormFields.FundsLoadingType);
  const isDirectLoad =
    watch(ManageBenefitLunchFormFields.FundsLoadingType) ===
    FundsLoadingTypes.DirectLoad;
  const isSubmitAndShowRedirectConfirmation =
    showRedirectConfirmation && isSubmitOnRedirectConfirmation;
  const isRequestableBenefit =
    benefitType === BenefitType.Lunch ||
    benefitType === BenefitType.Recreational;

  const handleSaveButton = values => {
    const newBenefitGroup = mapFormToBenefitGroup(
      values,
      benefitGroup,
      benefitType
    );

    if (detail.mode === slideoutMode.edit) {
      onSave(newBenefitGroup, !!isShowScheduledNotificationBanner);
    }
    if (detail.mode === slideoutMode.create) {
      onCreate(newBenefitGroup);
    }
  };

  const confirmCloseWithoutSaving = () => {
    setShowCloseConfirmation(false);
    onClose();
    reset();
  };

  const handleConfirmBenefitPopup = () => {
    confirmSaving();
    dispatch(goToPage(routerPaths.loadBenefit));
  };

  const handleCloseBenefitPopup = () => {
    confirmSaving();
    setShowCloseConfirmation(false);
  };

  const isShowFundLoadingType = benefitType === BenefitType.Lunch;
  const isShowScheduledNotificationBanner =
    (isShowFundLoadingType &&
      fundsLoadingType === FundsLoadingTypes.TopUpRequest &&
      benefitGroup?.fundsLoadingType !== fundsLoadingType &&
      benefitGroup?.isPendingScheduledLoadsForBenefit &&
      !benefitGroup?.isFundsLoadingTypePending) ||
    (benefitGroup?.fundsLoadingType == fundsLoadingType &&
      benefitGroup?.isFundsLoadingTypePending &&
      benefitGroup?.isPendingScheduledLoadsForBenefit);

  const isShowBasicNotificationBanner =
    (benefitGroup?.fundsLoadingType === fundsLoadingType &&
      benefitGroup?.isFundsLoadingTypePending &&
      !benefitGroup?.isPendingScheduledLoadsForBenefit) ||
    (benefitGroup?.fundsLoadingType !== fundsLoadingType &&
      !benefitGroup?.isFundsLoadingTypePending);

  const isShowNotificationBanner =
    detail.mode == slideoutMode.edit &&
    (isShowBasicNotificationBanner || isShowScheduledNotificationBanner);

  const confirmSaving = () => {
    setShowCloseConfirmation(false);
    handleSaveButton(getValues());
  };

  const slideoutTitle =
    detail.mode === slideoutMode.create
      ? t('components.manage_benefit_lunch.detail_create_title')
      : t('components.manage_benefit_lunch.detail_view_title');

  const handleCloseBenefitGroupDetailActions = () =>
    isDirty && detail.mode !== slideoutMode.view
      ? setShowCloseConfirmation(true)
      : onCancel();

  const handleClickBenefitGroupDetailActions = () => {
    setShowConfirmationModalForExistingClients(false);
    if (
      (isDirectLoad ||
        (!isRequestableBenefit && showConfirmationModalForExistingClients)) &&
      showRedirectConfirmation
    ) {
      setShowCloseConfirmation(true);
      setIsSubmitOnRedirectConfirmation(true);
    } else {
      confirmSaving();
    }
  };

  const handleShowConfirmationModalForExistingClients = () => {
    if (!isLoading && customerData?.isEmployeesSet) {
      setShowConfirmationModalForExistingClients(true);
    } else {
      handleClickBenefitGroupDetailActions();
    }
  };

  const handleCloseDetailVirike = () =>
    isSubmitAndShowRedirectConfirmation
      ? handleCloseBenefitPopup()
      : setShowCloseConfirmation(false);

  const handleSubmitDetailVirike = () => {
    if (isSubmitAndShowRedirectConfirmation) {
      return handleConfirmBenefitPopup();
    }

    confirmCloseWithoutSaving();
  };

  useEffect(() => {
    setShowRedirectConfirmation(!!dirtyFields?.maxAmountPerPeriod);
  }, [!!dirtyFields?.maxAmountPerPeriod]);

  useEffect(() => {
    if (
      (!isLoading && customerData?.isEmployeesSet) ||
      (!isLoading && customerData?.isBenefitsSet)
    ) {
      setIsExistingClients(true);
    } else {
      setIsExistingClients(false);
    }
  }, [isLoading]);

  return (
    <FormProvider {...form}>
      <Slideout
        title={slideoutTitle}
        expanded={detail.expanded}
        onClose={() =>
          isDirty && detail.mode !== slideoutMode.view
            ? setShowCloseConfirmation(true)
            : onClose()
        }
        actions={
          detail.mode !== slideoutMode.view ? (
            <BenefitGroupDetailActions
              saveDisabled={!isValid}
              onCancel={handleCloseBenefitGroupDetailActions}
              onClick={
                isExistingClients && detail.mode === slideoutMode.create
                  ? handleShowConfirmationModalForExistingClients
                  : handleClickBenefitGroupDetailActions
              }
            />
          ) : null
        }
        closeButtonId="close-benefit-group-slideout"
      >
        <BenefitGroupDetailContent
          detail={detail}
          isShowScheduledNotificationBanner={
            !!isShowScheduledNotificationBanner
          }
          isShowNotificationBanner={!!isShowNotificationBanner}
          benefitGroup={benefitGroup}
          dailyLimitOptions={dailyLimitOptions}
          benefitType={benefitType}
          onEdit={onEdit}
        />
        <CloseBenefitDetailModal
          isOpen={showCloseConfirmation}
          handleCloseModal={handleCloseDetailVirike}
          handleSubmit={handleSubmitDetailVirike}
          isSubmitAndShowRedirectConfirmation={
            isSubmitAndShowRedirectConfirmation
          }
        />
        <ConfirmationModal
          isOpen={showConfirmationModalForExistingClients}
          handleClose={() => setShowConfirmationModalForExistingClients(false)}
          handleSuccess={handleClickBenefitGroupDetailActions}
          benefitType={benefitType}
        />
      </Slideout>
    </FormProvider>
  );
}
